import { $ZodError } from 'zod/v4/core'

import APP_TOKEN from './app-token'
import getErrorMessage from './error-message'

// Нормалізована помилка бекенду кабінету — структурний відповідник згенерованого
// ApiResponseError кожного кабінету (тримаємо тут, щоб обидва мали єдину обробку).
export type CabinetApiError = {
	// Оригінальна кинута помилка (до нормалізації). handleSentryLog кладе її в Error.cause,
	// щоб Sentry (linkedErrors) показував справжній стек, а не місце створення wrapper-а.
	readonly cause?: unknown
	readonly code: string
	readonly details?: readonly string[]
	readonly message: string
	readonly status_code: number
}

// AbortError → синтетичний system_error/500 (нижче). Це не реальна серверна 500, а скасований
// браузером/TanStack in-flight запит (unload/навігація/анмаунт) — очікувано, у Sentry не шлемо.
const ABORTED_REQUEST_MESSAGE = 'Запит було скасовано'

// Повідомлення браузерних TypeError про мережевий збій fetch. Такий самий транзієнт,
// як AbortError (Wi-Fi зник, VPN, обрив) — сервер не винен, у Sentry не шлемо.
// Матчимо точні рядки, не instanceof TypeError: TypeError з коду (справжній баг) мусить
// лишатися видимим у Sentry.
// Джерела: 'Failed to fetch' — Chrome; 'NetworkError…' — Firefox; решта — WebKit
// (Safari/iOS): загальний 'Load failed' + NSURLError-локалізації ('cancelled', 'The …'),
// що просочуються у message fetch-TypeError замість нього.
const NETWORK_ERROR_MESSAGES = new Set([
	'cancelled',
	'Failed to fetch',
	'Load failed',
	'NetworkError when attempting to fetch resource.',
	'The Internet connection appears to be offline.',
	'The network connection was lost.',
	'The request timed out.',
])

// Спільний предикат, щоб репортери обох кабінетів (handleSentryLog) відсіювали транзієнтні
// підвипадки system_error (аборт, мережевий збій), лишаючи справжні system_error
// (zod-дрейф, реальні 500) видимими.
export const isTransientRequestError = (error: { code: string; message: string }): boolean =>
	error.code === 'system_error' && (error.message === ABORTED_REQUEST_MESSAGE || NETWORK_ERROR_MESSAGES.has(error.message))

export type CabinetClient = {
	interceptors: { error: { use: (interceptor: (error: unknown, response: Response | undefined) => unknown) => void } }
	setConfig: (config: { baseUrl?: string; headers?: Record<string, string> }) => unknown
}

// Мінімальні структурні типи згенерованого openapi-ts клієнта/схеми — лише те, що чіпаємо
// тут, щоб не залежати від @hey-api-типів кожного кабінету.
type ApiErrorSchema = {
	safeParse: (value: unknown) => { data: CabinetApiError; success: true } | { error: unknown; success: false }
}

// setupCabinetClient — спільний bootstrap API-клієнта кабінетів: X-App-Token (ingress-ґейт)
// + нормалізація будь-якої кинутої помилки у ApiResponseError, яку далі логить QueryCache.onError.
// zApiResponseError — згенерована zod-схема конкретного кабінету (структурно однакова).
const setupCabinetClient = (client: CabinetClient, zApiResponseError: ApiErrorSchema): void => {
	client.setConfig({
		baseUrl: import.meta.env.MODE === 'e2e' ? 'http://localhost:8080' : undefined,
		headers: { Accept: 'application/json', 'X-App-Token': APP_TOKEN },
	})

	client.interceptors.error.use((error, response) => {
		// Zod-фейл валідації відповіді (schema drift / deploy skew): раніше віддавали сирий
		// $ZodError і користувач бачив issues-JSON. Тепер — дружнє повідомлення; сигнал у Sentry
		// лишається (QueryCache.onError логить system_error, якого нема в IGNORED_ERRORS), дрейф
		// відрізняється за message. zodErrorsIntegration-групування свідомо міняємо на UX.
		if (error instanceof $ZodError) {
			return { cause: error, code: 'system_error', message: 'Неочікувана відповідь сервера', status_code: 500 } satisfies CabinetApiError
		}
		if (error instanceof DOMException && error.name === 'AbortError') {
			return { code: 'system_error', message: ABORTED_REQUEST_MESSAGE, status_code: 500 } satisfies CabinetApiError
		}

		const parsed = zApiResponseError.safeParse(error)
		if (parsed.success) return parsed.data

		if (!import.meta.env.PROD) {
			console.error('API error parsing failed:', parsed.error)
			console.error('Original error:', error)
		}

		const status = response?.status ?? 500
		// Тільки справжні HTTP-помилки (не-2xx) отримують загальне «сервера {status}». 2xx, що дійшов
		// сюди, не зміг прочитати/розпарсити тіло (обрив мережі на мобільному, битий body) — лишаємо
		// оригінальне повідомлення, щоб придушення мережевого шуму Sentry (ignoreErrors / beforeSend)
		// відкинуло транзієнти.
		const message = response && !response.ok ? `Помилка при запиті до сервера ${String(response.status)}` : getErrorMessage(error, false)

		return { cause: error, code: 'system_error', message, status_code: status } satisfies CabinetApiError
	})
}

export default setupCabinetClient
